import { useMemo, useState } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '@/api/client';
import { opsApi } from '@/api/ops';
import type { EnvironmentTenant } from '@/api/opsTypes';
import { PERMISSIONS, type ScoreboardRow } from '@/api/types';
import { hasPermission } from '@/auth/session';
import { Select } from '@/components/Select';
import { StatusChip } from '@/components/ops/Badges';
import { PageHeader, Section } from '@/components/ops/Layout';
import { RequirePermission } from '@/components/ops/PermissionGate';
import { ProblemCallout, QueryState } from '@/components/ops/Problem';
import { formatBytes, formatCores, formatTimestamp } from '@/ops/format';
import { REPLICA } from '@/ops/live';
import { useEnvironments } from '@/ops/useEnvironment';
import { formatWhen } from '@/pages/AccountsPage';

export type TenantSort = 'activity' | 'name' | 'people' | 'data' | 'cpu';

const SORTS: { value: TenantSort; label: string }[] = [
  { value: 'activity', label: 'Last active' },
  { value: 'name', label: 'Name' },
  { value: 'people', label: 'Most people' },
  { value: 'data', label: 'Most data' },
  { value: 'cpu', label: 'Most server use' },
];

/** Databases + files, or null when the databases were never measured (files alone would understate it). */
export function dataStored(t: EnvironmentTenant): number | null {
  const db = t.footprint?.db_bytes;
  return db === null || db === undefined ? null : db + (t.storage_used_bytes ?? 0);
}

/** Filter + sort, pure. An unknown figure sorts LAST whichever way — never as a zero at the top or bottom. */
export function arrangeTenants(
  tenants: EnvironmentTenant[],
  { search, showInternal, sort }: { search: string; showInternal: boolean; sort: TenantSort },
): EnvironmentTenant[] {
  const q = search.trim().toLowerCase();
  const byNumber = (value: (t: EnvironmentTenant) => number | null | undefined) =>
    (a: EnvironmentTenant, b: EnvironmentTenant) => {
      const x = value(a);
      const y = value(b);
      if (x == null && y == null) return 0;
      if (x == null) return 1;
      if (y == null) return -1;
      return y - x;
    };
  const compare: Record<TenantSort, (a: EnvironmentTenant, b: EnvironmentTenant) => number> = {
    activity: (a, b) => (b.last_activity_at ?? '').localeCompare(a.last_activity_at ?? ''),
    name: (a, b) => (a.name ?? '').localeCompare(b.name ?? '', 'en', { sensitivity: 'base' }),
    people: byNumber((t) => t.user_count),
    data: byNumber(dataStored),
    cpu: byNumber((t) => t.footprint?.cpu_cores.value),
  };
  return tenants
    .filter((t) => showInternal || !t.is_internal)
    .filter((t) => !q || (t.name ?? '').toLowerCase().includes(q) || t.tenant_id.startsWith(q))
    .sort(compare[sort]);
}

/**
 * THE tenants list (2026-09-30 redesign): every tenant on every environment, from the console's saved copy — so it
 * renders with the platform down, and says how old it is. One read per environment, never one per tenant. Data stored
 * is measured; server use is an estimate (the tenant's share of the environment over the last 7 days) and says so.
 */
export function TenantsPage() {
  const environments = useEnvironments();
  const [envFilter, setEnvFilter] = useState('');
  const [search, setSearch] = useState('');
  const [showInternal, setShowInternal] = useState(false);
  const [sort, setSort] = useState<TenantSort>('activity');
  const canProvision = hasPermission(PERMISSIONS.tenantProvision);
  const canHealth = hasPermission(PERMISSIONS.telemetryRead);
  const canOpenJobs = hasPermission(PERMISSIONS.tenantProvision);

  const envs = environments.data?.environments ?? [];
  const selected = envFilter ? envs.filter((e) => e.key === envFilter) : envs;

  const lists = useQueries({
    queries: selected.map((e) => ({
      queryKey: ['ops', 'env-tenants', e.key],
      queryFn: () => opsApi.tenants.list(e.key),
      ...REPLICA,
    })),
  });
  // A tenant never scored is ABSENT from this map — its cell says so, it is never a zero.
  const scoreboard = useQuery({ queryKey: ['scoreboard'], queryFn: () => api.scores.scoreboard(), enabled: canHealth, retry: false });
  const scores = useMemo(() => new Map((scoreboard.data ?? []).map((s) => [s.tenant_id, s])), [scoreboard.data]);

  const all = useMemo(() => lists.flatMap((l) => l.data?.tenants ?? []), [lists]);
  const rows = useMemo(() => arrangeTenants(all, { search, showInternal, sort }), [all, search, showInternal, sort]);
  const internalHidden = showInternal ? 0 : all.filter((t) => t.is_internal).length;
  const oldestSync = all.reduce<string | null>((oldest, t) => (!oldest || t.synced_at < oldest ? t.synced_at : oldest), null);

  const failed = lists.map((l, i) => ({ env: selected[i]?.key, error: l.error })).filter((f) => f.error);
  const loading = lists.some((l) => l.isPending);

  return (
    <RequirePermission permission={PERMISSIONS.hostsRead} what="The tenants list">
      <PageHeader
        title="Tenants"
        subtitle="Every tenant on every environment. Open one for its usage, people, health, billing and credits."
        actions={canProvision && <Link to="/ops/provisioning/new" className="btn btn-primary">Create a tenant</Link>}
      />

      <QueryState query={environments}>
        {() => (
          <>
            <div className="filterbar ops-filters">
              <Select
                label="Environment"
                inline
                value={envFilter}
                onChange={setEnvFilter}
                placeholder="All environments"
                options={envs.map((e) => ({ value: e.key, label: e.label || e.key }))}
              />
              <input
                className="input"
                style={{ maxWidth: 280 }}
                placeholder="Search by name or tenant id"
                aria-label="Search tenants"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
              <Select label="Sort by" inline value={sort} onChange={(v) => setSort(v as TenantSort)} options={SORTS} />
              <label className="checkbox-row t-label">
                <input type="checkbox" checked={showInternal} onChange={(e) => setShowInternal(e.target.checked)} />
                Include internal{internalHidden > 0 ? ` (${internalHidden} hidden)` : ''}
              </label>
            </div>

            {failed.map((f) => (
              <ProblemCallout key={f.env} error={f.error} title={`Tenants of ${f.env} could not be read`} />
            ))}

            <Section
              title={loading ? 'Tenants' : `${rows.length} tenant${rows.length === 1 ? '' : 's'}`}
              meta={loading ? 'loading…' : oldestSync ? `saved copy · oldest sync ${formatWhen(oldestSync)}` : undefined}
              flush
            >
              {rows.length === 0 && !loading ? (
                <div className="empty-state t-body">
                  {all.length === 0 ? 'No tenants on this selection yet.' : 'No tenant matches these filters.'}
                </div>
              ) : (
                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Tenant</th>
                        <th>Plan</th>
                        <th className="num">People</th>
                        <th className="num" title="Databases + files, measured by the collectors">Data stored</th>
                        <th className="num" title="Estimated: the tenant's share of its environment's CPU and memory over the last 7 days">
                          Server use <span className="t-caption muted">(est.)</span>
                        </th>
                        {canHealth && <th className="num" title="Health score 0–100; flags = warning signs raised">Health</th>}
                        <th>Last active</th>
                        <th>Set up</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((t) => (
                        <TenantRow
                          key={`${t.environment}-${t.tenant_id}`}
                          tenant={t}
                          showEnvironment={selected.length > 1}
                          score={canHealth ? scores.get(t.tenant_id) ?? null : undefined}
                          scoresFailed={scoreboard.isError}
                          canOpenJobs={canOpenJobs}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Section>
          </>
        )}
      </QueryState>
    </RequirePermission>
  );
}

function TenantRow({ tenant: t, showEnvironment, score, scoresFailed, canOpenJobs }: {
  tenant: EnvironmentTenant;
  showEnvironment: boolean;
  score: ScoreboardRow | null | undefined;
  /** The scoreboard read failed: a missing score is "could not be read", never "not scored yet". */
  scoresFailed: boolean;
  /** Setup jobs are tenant:provision (owner): other roles see the status without a link that lands on a refusal. */
  canOpenJobs: boolean;
}) {
  const data = dataStored(t);
  const cpu = t.footprint?.cpu_cores.value;
  const memory = t.footprint?.memory_bytes.value;
  return (
    <tr>
      <td>
        <Link to={`/ops/tenants/${encodeURIComponent(t.environment)}/${t.tenant_id}`} style={{ fontWeight: 600 }}>
          {t.name || '(unnamed)'}
        </Link>
        {t.is_internal && <span className="chip chip-muted" style={{ marginInlineStart: 6 }}>internal</span>}
        <div className="t-caption muted" title={`synced ${formatTimestamp(t.synced_at)}`}>
          {showEnvironment ? `${t.environment} · ` : ''}created {formatWhen(t.created_at)}
        </div>
      </td>
      <td>
        <span className="t-label">{t.plan_code ?? '—'}</span>{' '}
        {t.subscription_status ? <StatusChip status={t.subscription_status} /> : <span className="t-caption subtle">no subscription</span>}
      </td>
      <td className="num">
        {/* '?' not 0 — an unreadable count is not an empty tenant. */}
        {t.user_count === null ? '?' : t.user_count}
        <div className="t-caption muted">{t.active_user_count === null ? '? active' : `${t.active_user_count} active`}</div>
      </td>
      <td className="num" title={data === null ? 'The databases have not been measured yet' : undefined}>
        {data === null ? <span className="subtle">—</span> : formatBytes(data)}
        {/* A total without the files is a partial figure — say so rather than let it read as everything. */}
        {data !== null && t.storage_used_bytes === null && <div className="t-caption muted">files not measured</div>}
      </td>
      <td className="num" title={cpu == null ? 'Not enough samples in the last 7 days to estimate' : 'Estimated share over the last 7 days'}>
        {cpu == null ? <span className="subtle">—</span> : formatCores(cpu)}
        {memory != null && <div className="t-caption muted">{formatBytes(memory)}</div>}
      </td>
      {score !== undefined && (
        <td className="num">
          {score === null ? (
            scoresFailed
              ? <span className="note-warning" title="The scores could not be read — try again later">?</span>
              : <span className="subtle" title="Not scored yet">—</span>
          ) : score.unscored ? (
            <span className="muted" title="Nothing the score measures could be read">n/a</span>
          ) : (
            <span className="tabular">{Math.round(score.total)}</span>
          )}
          {score && score.risk_level > 0 && (
            <div className="t-caption" style={{ color: 'var(--warning)' }}>
              {score.risk_level} flag{score.risk_level === 1 ? '' : 's'}
            </div>
          )}
        </td>
      )}
      <td className="t-caption">{t.last_activity_at ? formatWhen(t.last_activity_at) : <span className="subtle">never</span>}</td>
      <td>
        {t.provisioning_job_id ? (
          canOpenJobs ? (
            <Link to={`/ops/provisioning/jobs/${t.provisioning_job_id}`}>
              <StatusChip status={t.provisioning_status ?? 'unknown'} />
            </Link>
          ) : (
            <StatusChip status={t.provisioning_status ?? 'unknown'} />
          )
        ) : (
          <span className="t-caption subtle">outside the console</span>
        )}
      </td>
    </tr>
  );
}
